// The TEAM PERSON DETAIL (Signal) — the selected User's card: identity head, responsibilities, access, and
// the "what they can sign off" list restating the authority profile's CLOSED capability keys in plain
// English. The switches are READ-ONLY state, not controls: the dashboard sets no field and commits no
// directory change (P-GATE) — the ONE mutation affordance here is "Suggest a change", a routed PROPOSAL
// (BUILD-DEC-115: any active human proposes; the agent can too) with a MANDATORY change description
// (the resolver's queue renders exactly that text — a blind review item is what N8/DS-APPROVALS-04
// forbid); the Operator resolves the commit in `identity` (A). No credential value, no raw address
// (G-140/G-058). Tokens only (G-181).

import { useState } from 'react';
import { Btn } from '../primitives.tsx';
import { Avatar } from '../../app/Avatar.tsx';
import { ownerInitials } from '../owner-display.ts';
import {
  ACCESS_LEVEL_LABEL,
  ACCESS_LEVEL_ORDER,
  authorityRows,
  firstNameOf,
  type EditablePersonPatch,
} from './team-directory.ts';
import type { RouteResult } from '../api-client.ts';
import { ContactSection } from './TeamContacts.tsx';
import type { ContactRow, DirectoryRole, UserRow } from '../know-projections.ts';

/** The inline SUGGEST-A-CHANGE form — the human's own words, mandatory (N8), routed to the Operator. */
function SuggestChangeForm({
  user,
  onSubmit,
  onCancel,
}: {
  readonly user: UserRow;
  readonly onSubmit: (summary: string) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [summary, setSummary] = useState('');
  const empty = summary.trim().length === 0;
  return (
    <div className="tm-propose" data-propose-form={user.id}>
      <textarea
        rows={3}
        value={summary}
        aria-label={`Describe the change: ${user.displayName}`}
        placeholder="Describe the change — e.g. a new address, an access change, someone joining or leaving."
        onChange={(event) => setSummary(event.target.value)}
      />
      <div className="tm-propose-actions">
        <Btn
          kind="primary"
          sm
          disabled={empty}
          ariaLabel={`Send the proposal: ${user.displayName}`}
          onClick={() => {
            if (!empty) onSubmit(summary.trim());
          }}
        >
          Send to Operator
        </Btn>
        <Btn kind="ghost" sm ariaLabel="Cancel the proposal" onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

/** The read-only sign-off list — one switch-look STATE indicator per capability key the profile
 *  carries (disabled: nothing commits from this screen; the honest note below says where changes go). */
function SignOffList({ user }: { readonly user: UserRow }): React.JSX.Element {
  const rows = authorityRows(user.authorityProfile);
  const first = firstNameOf(user);
  return (
    <div className="tm-sec">
      <div className="tm-sec-head">
        <span className="mono-label">What {first} can sign off</span>
      </div>
      <ul className="tm-auth" data-authority-profile>
        {rows.map((row) => (
          <li key={row.key} className="tm-auth-row">
            <button
              type="button"
              role="switch"
              aria-checked={row.granted}
              aria-label={row.label}
              disabled
              className="tm-switch"
            >
              <span className="kn" />
            </button>
            <span className="tm-auth-copy">
              <span className="tm-auth-label">{row.label}</span>
              {row.sub !== null ? <span className="tm-auth-sub">{row.sub}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      <span className="tm-sec-note">
        These are set by whoever holds “Save team changes” — every change is credential-enforced and
        attributed. Ritona can suggest one, never commit it.
      </span>
    </div>
  );
}

/** The name, editable in place for a key-holder (BUILD-DEC-128). Commits on blur or Enter, and only
 *  when the name actually CHANGED and is non-blank — identity refuses a blank rename anyway, but a
 *  no-op round trip would flash a pointless "saved" receipt. Escape abandons the edit. */
function NameField({
  user,
  onRename,
}: {
  readonly user: UserRow;
  readonly onRename: (displayName: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(user.displayName);
  const commit = (): void => {
    const next = draft.trim();
    if (next.length === 0 || next === user.displayName) {
      setDraft(user.displayName);
      return;
    }
    onRename(next);
  };
  return (
    <input
      type="text"
      className="tm-card-name tm-name-input"
      value={draft}
      aria-label={`Name: ${user.displayName}`}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') setDraft(user.displayName);
      }}
    />
  );
}

/** The card head — identity + the ONE mutation affordance on a person (the suggest toggle). */
function PersonHead({
  user,
  canEditContacts,
  onToggleForm,
  onRename,
}: {
  readonly user: UserRow;
  readonly canEditContacts: boolean;
  readonly onToggleForm: () => void;
  readonly onRename: (displayName: string) => void;
}): React.JSX.Element {
  return (
    <div className="tm-card-head">
      <Avatar initials={ownerInitials(user.displayName)} size="lg" />
      <span className="tm-card-id">
        {canEditContacts ? (
          <NameField user={user} onRename={onRename} />
        ) : (
          <span className="tm-card-name">{user.displayName}</span>
        )}
        <span className="tm-card-sub">{user.status}</span>
      </span>
      {/* The propose lane survives ONLY for someone without the commit key (BUILD-DEC-127): a
          key-holder edits directly above, and proposing to yourself was the ceremony Paul removed. */}
      <span data-propose-change={user.id} style={{ display: canEditContacts ? 'none' : undefined }}>
        <Btn
          kind="ghost"
          sm
          ariaLabel={`Propose a directory change: ${user.displayName}`}
          onClick={onToggleForm}
        >
          Suggest a change
        </Btn>
      </span>
    </div>
  );
}

/** The fail-closed truth about a suspended person — stated on their card, never by hiding them. */
function SuspendedBanner({ user }: { readonly user: UserRow }): React.JSX.Element | null {
  if (user.status !== 'suspended') return null;
  return (
    <div className="tm-banner">
      Suspended — Ritona fails their access closed: nothing they send is accepted. Their history
      stays in the audit log.
    </div>
  );
}

/** The human-authored paragraph Ritona reads when it needs to understand who does what. */
function ResponsibilitiesSection({
  user,
  canEdit,
  onSave,
}: {
  readonly user: UserRow;
  readonly canEdit: boolean;
  readonly onSave: (responsibilities: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(user.responsibilities);
  const next = draft.trim();
  const changed = next.length > 0 && next !== user.responsibilities;
  return (
    <div className="tm-sec">
      <div className="tm-sec-head">
        <span className="mono-label">What they do</span>
        <span className="tm-sec-hint">Ritona reads this when understanding and routing work</span>
      </div>
      {canEdit ? (
        <>
          <textarea
            className="tm-responsibilities"
            rows={4}
            value={draft}
            aria-label={`What ${user.displayName} does`}
            placeholder="e.g. Handles production, quality control and scheduling."
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="tm-propose-actions">
            <Btn
              kind="ghost"
              sm
              disabled={!changed}
              ariaLabel={`Save what ${user.displayName} does`}
              onClick={() => changed && onSave(next)}
            >
              Save what they do
            </Btn>
          </div>
        </>
      ) : (
        <p className="tm-responsibilities-read">
          {user.responsibilities.trim().length > 0
            ? user.responsibilities
            : 'What they do has not been added yet.'}
        </p>
      )}
    </div>
  );
}

/** Ritona permissions, visibly separate from the person's real company responsibilities. */
function AccessLevelSection({
  user,
  canEdit,
  onSetAccessLevel,
}: {
  readonly user: UserRow;
  readonly canEdit: boolean;
  readonly onSetAccessLevel: (role: DirectoryRole) => void;
}): React.JSX.Element {
  return (
    <div className="tm-sec">
      <div className="tm-sec-head">
        <span className="mono-label">Access level</span>
        <span className="tm-sec-hint">Controls what this person can do inside Ritona</span>
      </div>
      {canEdit ? (
        <select
          className="tm-role-select"
          value={user.role}
          aria-label={`Access level: ${user.displayName}`}
          onChange={(event) => onSetAccessLevel(event.target.value as DirectoryRole)}
        >
          {ACCESS_LEVEL_ORDER.map((role) => (
            <option key={role} value={role}>
              {ACCESS_LEVEL_LABEL[role]}
            </option>
          ))}
        </select>
      ) : (
        <span className="tm-role-read">{ACCESS_LEVEL_LABEL[user.role]}</span>
      )}
      <span className="tm-sec-note">
        This is a permissions setting. It is not {firstNameOf(user)}’s job or role in the company.
        {canEdit ? ' Changing access resets the sign-off list to that level’s defaults.' : ''}
      </span>
    </div>
  );
}

/** The card's stacked sections: responsibilities, access, contacts, and sign-off. */
function PersonSections({
  user,
  contacts,
  canEdit,
  onAddAddress,
  onRetireAddress,
  onEditPerson,
}: {
  readonly user: UserRow;
  readonly contacts: readonly ContactRow[] | undefined;
  readonly canEdit: boolean;
  readonly onAddAddress: (userId: string, address: string) => void;
  readonly onRetireAddress: (channelIdentityId: string) => void;
  readonly onEditPerson: (userId: string, patch: EditablePersonPatch) => void;
}): React.JSX.Element {
  return (
    <>
      <ResponsibilitiesSection
        user={user}
        canEdit={canEdit}
        onSave={(responsibilities) => onEditPerson(user.id, { responsibilities })}
      />
      <AccessLevelSection
        user={user}
        canEdit={canEdit}
        onSetAccessLevel={(role) => onEditPerson(user.id, { role })}
      />
      {contacts !== undefined ? (
        <ContactSection
          user={user}
          contacts={contacts}
          canEdit={canEdit}
          onAdd={(address) => onAddAddress(user.id, address)}
          onRetire={onRetireAddress}
        />
      ) : null}
      <SignOffList user={user} />
    </>
  );
}

/** The selected User's detail card. The propose OUTCOME note is owned ABOVE the reload remount
 *  boundary (the N5 remount lesson) and renders in the surface's head strip, not here. */
export function TeamPersonDetail({
  user,
  contacts,
  canEditContacts,
  onProposeChange,
  onAddAddress,
  onRetireAddress,
  onEditPerson,
}: {
  readonly user: UserRow;
  /** This person's addresses, or undefined when the owning module did not allow this viewer to see
   *  them (IFC-IDENT-018) — the section then renders nothing at all rather than an empty list. */
  readonly contacts: readonly ContactRow[] | undefined;
  readonly canEditContacts: boolean;
  readonly onProposeChange: (userId: string, summary: string) => Promise<RouteResult>;
  readonly onAddAddress: (userId: string, address: string) => void;
  readonly onRetireAddress: (channelIdentityId: string) => void;
  readonly onEditPerson: (userId: string, patch: EditablePersonPatch) => void;
}): React.JSX.Element {
  const [formOpen, setFormOpen] = useState(false);
  const propose = (summary: string): void => {
    setFormOpen(false);
    // The outcome note is set by the owner above; the api client resolves a transport crash to a
    // refusal-shaped RouteResult, so no outcome is ever silent.
    void onProposeChange(user.id, summary);
  };
  return (
    <section aria-label="Person" className="tm-detail">
      <div className="tm-card">
        <PersonHead
          user={user}
          canEditContacts={canEditContacts}
          onToggleForm={() => setFormOpen((open) => !open)}
          onRename={(displayName) => onEditPerson(user.id, { displayName })}
        />
        <SuspendedBanner user={user} />
        {formOpen ? (
          <SuggestChangeForm user={user} onSubmit={propose} onCancel={() => setFormOpen(false)} />
        ) : null}
        <PersonSections
          user={user}
          contacts={contacts}
          canEdit={canEditContacts}
          onAddAddress={onAddAddress}
          onRetireAddress={onRetireAddress}
          onEditPerson={onEditPerson}
        />
      </div>
    </section>
  );
}
